import { createContext, useContext, useState, useEffect, useCallback } from "react"
import { STEPS_BY_ROLE } from "../utils/tutorialSteps"

const TutorialContext = createContext(null)

const KEY = "recordlogs_tutorial_active_v1"

function load() {
  try {
    const raw = localStorage.getItem(KEY)
    return raw ? JSON.parse(raw) : null
  } catch { return null }
}

function persist(val) {
  try {
    if (val) localStorage.setItem(KEY, JSON.stringify(val))
    else localStorage.removeItem(KEY)
  } catch {}
}

export function TutorialProvider({ children }) {
  const [active, setActiveRaw] = useState(() => load())

  // Sync to localStorage whenever active changes — kept out of `set` so there
  // are no stale-closure issues when timers fire after endTutorial.
  useEffect(() => { persist(active) }, [active])

  // `set` uses React's functional-updater form so it always sees the latest
  // state, regardless of when a stale closure calls it.
  const set = useCallback((next) => {
    setActiveRaw((current) => typeof next === "function" ? next(current) : next)
  }, [])

  const startTutorial = useCallback((roleId) => {
    set({ roleId, stepIndex: 0 })
  }, [set])

  const nextStep = useCallback(() => {
    set((s) => {
      if (!s) return null
      const steps = STEPS_BY_ROLE[s.roleId] ?? []
      const next = s.stepIndex + 1
      if (next >= steps.length) return null
      return { ...s, stepIndex: next }
    })
  }, [set])

  const prevStep = useCallback(() => {
    set((s) => s ? { ...s, stepIndex: Math.max(0, s.stepIndex - 1) } : null)
  }, [set])

  const endTutorial = useCallback(() => set(null), [set])

  const steps = active ? (STEPS_BY_ROLE[active.roleId] ?? []) : []
  const currentStep = active ? (steps[active.stepIndex] ?? null) : null
  const isActive = Boolean(active && currentStep)

  return (
    <TutorialContext.Provider value={{
      isActive,
      active,
      currentStep,
      steps,
      startTutorial,
      nextStep,
      prevStep,
      endTutorial,
    }}>
      {children}
    </TutorialContext.Provider>
  )
}

export function useTutorial() {
  const ctx = useContext(TutorialContext)
  if (!ctx) throw new Error("useTutorial must be inside TutorialProvider")
  return ctx
}
